import { ref } from 'vue';
import { useToast } from 'vue-toast-notification';

import moment from 'moment-timezone';

import i18n from '@/i18n';

import type { MissionPeriod } from '@/types/mission';
import type { DateRange } from './types';

import { TABLE_DATE_FORMAT } from './constants';
import { getHourLabelFromValue } from './helper';

const timestampToDate = (titmestamp: number) =>
	moment(titmestamp).format(TABLE_DATE_FORMAT);

export const useDateRange = () => {
	const dateRanges = ref<DateRange[]>([]);

	const toaster = useToast();

	const toastError = (message: string) => {
		toaster?.open({
			message,
			type: 'error',
			duration: 5000,
		});
	};

	const setWithPeriods = (
		periods: MissionPeriod[] | undefined,
		extend = false,
		_duplicate = false
	) => {
		if (!periods) return;
		const safePeriods = periods.filter(
			(period: MissionPeriod) => period.slots.all?.length > 0
		);
		if (safePeriods.length === 0) return;
		if (extend) {
			const lastPeriod = safePeriods[safePeriods.length - 1];
			const lastPeriodEnd = moment(lastPeriod.end);
			const weekendIncluded = lastPeriod.weekendIncluded ?? false;
			const originalSlots = safePeriods[0].slots.all;

			if (!originalSlots?.length) {
				console.error('No slots found in first period');
				return;
			}

			const startTime = originalSlots[0].from.toString();
			const endTime = originalSlots[0].to.toString();

			// Check if last day is Friday and weekend not included
			const isFriday = lastPeriodEnd.isoWeekday() === 5;
			const isWeekendNotIncluded = !weekendIncluded;
			const PERIOD_DURATION_DAYS = 7;

			const extendStart =
				isFriday && isWeekendNotIncluded
					? lastPeriodEnd.add(3, 'days').startOf('day').valueOf() // Start Monday
					: lastPeriodEnd.add(1, 'day').startOf('day').valueOf(); // Start next day

			const extendEnd = moment(extendStart)
				.add(PERIOD_DURATION_DAYS, 'days')
				.endOf('day')
				.valueOf();

			dateRanges.value = [
				{
					startDate: extendStart,
					endDate: extendEnd,
					startTime,
					endTime,
					weekendIncluded,
				},
			];

			return;
		}

		dateRanges.value = safePeriods.flatMap(
			(period: MissionPeriod): DateRange[] =>
				period.slots.all.map(
					(slot): DateRange => ({
						startDate: period.start,
						endDate: period.end,
						startTime: slot.from.toString(),
						endTime: slot.to.toString(),
						weekendIncluded: period.weekendIncluded ?? false,
					})
				)
		);
	};

	const validate = (errorOnPastDate = false) => {
		// Use to trigger one toastError at once and return it at the end
		let isValid = true;
		dateRanges.value.forEach((range) => {
			const errorFields: string[] = [];

			// Make sure date and time fileds are set.
			['startDate', 'endDate', 'startTime', 'endTime'].forEach((field) => {
				// isValid is not used here to validate missing fields anyway
				if (!range[field as keyof DateRange] && !errorFields.includes(field)) {
					errorFields.push(field);
					toastError(i18n.global.t(`page.order.error.${field}`));
					isValid = false;
				}
			});

			// Check that statDate is before endDate. && !error.value.dateRanges print one error at a time to not fould the user
			if (
				range.startDate &&
				range.endDate &&
				range.startDate > range.endDate &&
				isValid
			) {
				toastError(
					i18n.global.t('page.order.error.startDateBeforeEndDate', {
						startDate: timestampToDate(range.startDate),
						endDate: timestampToDate(range.endDate),
					})
				);
				isValid = false;
			}

			// Check that statTime is before endTime
			if (
				range.startDate &&
				range.endDate &&
				moment(range.startDate).isSame(moment(range.endDate)) &&
				Number(range.startTime) > Number(range.endTime) &&
				isValid
			) {
				toastError(
					i18n.global.t('page.order.error.startTimeBeforeEndTime', {
						startTime: getHourLabelFromValue(range.startTime),
						endTime: getHourLabelFromValue(range.endTime),
					})
				);
				isValid = false;
			}

			// Check that statDate is after now
			const momentStartDate = moment(range.startDate)
				.hour(Number.parseInt(range.startTime?.slice(0, -2) ?? '0', 10))
				.minute(Number.parseInt(range.startTime?.slice(-2) ?? '0', 10));
			if (
				range.startDate &&
				momentStartDate.valueOf() < Date.now() &&
				isValid &&
				errorOnPastDate
			) {
				toastError(
					i18n.global.t('page.order.error.startDateBeforeNow', {
						startTime: getHourLabelFromValue(range.startTime),
						endTime: getHourLabelFromValue(range.endTime),
					})
				);
				isValid = false;
			}
		});
		return isValid;
	};

	return { dateRanges, validate, setWithPeriods };
};
